import React, { useCallback, useEffect, useRef, useState } from 'react';
import { HashRouter as Router, Navigate, Route, Routes } from "react-router-dom";
import { generateRoutes } from './routes';
import { useDispatch, useSelector } from 'react-redux';
import ReactGA from 'react-ga4';
import { resetValidateRefreshToken, validateInternalToken, validateRefreshToken, validateToken } from './redux/actions/auth/token';
import setSessionExpiry from './utils/sessionExpiry';
import { logout } from './redux/actions/auth/auth';
import { toast } from 'react-toastify';
import { useFormErrorValidationResponse } from './utils/context/FormErrorContext';
import { SaveResultState } from './redux/reducers/getSaveData/saveResultReducer';
import Modal from './components/common/Modal/Modal';
import { useTranslation } from 'react-i18next';
import Button from './components/common/Button/Button';
import { NavigationProvider } from './utils/context/NavigationContext';
import { selectAuthValidateExternalToken, selectAuthValidateInternalToken, selectAuthValidateRefreshToken, selectPostIncomeUnit, selectPutIncomeUnit, selectSaveResult } from './redux/selectors/exportSelectorPaths';
import trackCustomGA4Event from './utils/GA4EventHelpers/trackCustomGA4Event';
import formatUnixTimestamp from './utils/unixToLocalDateTime';
import LoadingSpinner from './components/common/LoadingSpinner/LoadingSpinner';
import { useFormWarningValidationResponse } from './utils/context/FormWarningContext';

function App(): JSX.Element {
	const { t } = useTranslation('system');
	const dispatch = useDispatch()
	const [auth, setAuth] = useState(false);
	const [sessionExpired, setSessionExpired] = useState(false);
	const [validateExternalTokenReqSent, setValidateExternalTokenReqSent] = useState(false);
	const [validateInternalTokenReqSent, setValidateInternalTokenReqSent] = useState(false);
	const [validateRefreshTokenReqSent, setValidateRefreshTokenReqSent] = useState(false);
	const [isInternalUser, setIsInternalUser] = useState(false);
	const saveResultState: SaveResultState = useSelector(selectSaveResult);
	const validateTokenStore = useSelector(selectAuthValidateExternalToken);
	const validateInternalTokenStore = useSelector(selectAuthValidateInternalToken);
	const refreshTokenStore = useSelector(selectAuthValidateRefreshToken);
	const updateUnitState = useSelector(selectPutIncomeUnit)
	const createUnitState = useSelector(selectPostIncomeUnit);
	const internalUserVariable = process.env.REACT_APP_IS_INTERNAL_USER;
	const isLocalVariable = process.env.REACT_APP_IS_LOCAL;

	const [showSessionModal, setShowSessionModal] = useState<"hide" | "extend" | "expired">("hide");
	const [timerIsSet, setTimerIsSet] = useState(false);
	const ExtendTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const ExpireTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	useFormErrorValidationResponse(saveResultState.result ?? []);
	useFormWarningValidationResponse(saveResultState.result ?? []);

	useFormErrorValidationResponse(updateUnitState.result ?? []);
	useFormWarningValidationResponse(updateUnitState.result ?? []);

	useFormErrorValidationResponse(createUnitState.result ?? []);
	useFormWarningValidationResponse(createUnitState.result ?? []);
	const logoutUser = useCallback(() => {
		dispatch(logout())
		localStorage.clear();
		setAuth(false);
	}, [dispatch]);

	const setTimers = useCallback(() => {
		const timeLeft = setSessionExpiry();
		ExtendTimer.current && clearTimeout(ExtendTimer.current)
		ExpireTimer.current && clearTimeout(ExpireTimer.current)
		if (timeLeft <= 0) {
			if (!sessionExpired) {
				setSessionExpired(true);
				toast.error("Session expired");
				logoutUser();
			}
			setShowSessionModal("expired");
		} else {
			if (sessionExpired) {
				setSessionExpired(false);
			}
			if (timeLeft > 300000) {
				ExtendTimer.current = setTimeout(() => {
					setShowSessionModal("extend");
				}, timeLeft - 300000);
			} else if (timeLeft <= 300000 && timeLeft > 0) {
				setShowSessionModal("extend");
			}
			ExpireTimer.current = setTimeout(() => {
				setSessionExpired(true);
				logoutUser();
				setShowSessionModal("expired");
			}, timeLeft);
		}
		setTimerIsSet(true);
	}, [logoutUser, sessionExpired]);

	useEffect(() => {
		// set Custom Definition user_type and property_code to track internal vs external user
		ReactGA.gtag("set", "user_properties", {
			user_type: (internalUserVariable === "1") ? "internal" : "external",
		})

		// eslint-disable-next-line
		if (internalUserVariable == "0") {
			// Validate amp token only for external users
			// eslint-disable-next-line
			if (isLocalVariable == "0") {
				const splitRoute = window.location.href.split('/')
				const localToken = window.localStorage.getItem("authToken")
				const alreadyHasToken = localToken !== null && setSessionExpiry() > 0
				const token = alreadyHasToken ? localToken : splitRoute[splitRoute.length - 1];
				const refreshTokenArr = window.location.href.split("refresh=");
				if (refreshTokenArr.length === 2) {
					window.localStorage.setItem("refreshToken", refreshTokenArr[1]);
				}

				if (token) {
					const parsedToken = alreadyHasToken ? token : token.split('?')[0]
					dispatch(validateToken(parsedToken));
					setValidateExternalTokenReqSent(true);
				}
				else {
					toast.error("Not Authorized, redirecting...");
					window.location.href = `${process.env.REACT_APP_AMP_URL}#/auth/home/reg-user`;
				}
			}
			else {
				dispatch(validateToken(process.env.REACT_APP_LOCAL_TOKEN as string));
				setValidateExternalTokenReqSent(true);
			}
		}
		// eslint-disable-next-line
		else if (internalUserVariable == "1") {
			const splitRoute = window.location.href.split('/');
			const localToken = window.localStorage.getItem("authToken")
			const token = splitRoute[splitRoute.length - 2];
			const roll = splitRoute[splitRoute.length - 1];
			if (roll.length === 15 && !isNaN(Number(roll))) {
				sessionStorage.setItem("rollNumber", roll);
				if (!validateInternalTokenReqSent) {
					dispatch(validateInternalToken(token));
					setValidateInternalTokenReqSent(true);
				}
			} else if (!validateInternalTokenReqSent) {
				if (localToken) {
					dispatch(validateInternalToken(localToken));
					setValidateInternalTokenReqSent(true);
				} else {
					toast.error(t("noToken") as string);
				}
			}
		}

		const reloadingSessionKey = "isReloading";
		// mark as not reloading initially
		sessionStorage.setItem(reloadingSessionKey, "false");

		const handleBeforeUnload = () => {
			const isReloading = sessionStorage.getItem(reloadingSessionKey) === "true";

			if (!isReloading) {
				trackCustomGA4Event("app_exit",
					{
						path: window.location.pathname,
						exit_method: "tab_close"
					});
			}
		};

		const handleReload = () => {
			sessionStorage.setItem(reloadingSessionKey, "true");
		};

		window.addEventListener("beforeunload", handleBeforeUnload);
		// Capture common refresh triggers
		window.addEventListener("keydown", (e) => {
			if (e.key === "F5" || (e.ctrlKey && e.key === "r")) {
				handleReload();
			}
		});

		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload);
		};
		// eslint-disable-next-line
	}, []);

	useEffect(() => {
		if (validateExternalTokenReqSent) {
			if (validateTokenStore.valid) {
				validateTokenStore.token && window.localStorage.setItem("authToken", validateTokenStore.token);
				validateTokenStore.exp && window.localStorage.setItem("sessionExp", String(validateTokenStore.exp));
				validateTokenStore.validationToken && window.localStorage.setItem("validationToken", validateTokenStore.validationToken);
				(localStorage.getItem("sessionExp") !== null) && setTimers();
				setAuth(true);
				setValidateExternalTokenReqSent(false);
				dispatch({ type: "SAVE_REFRESH_TOKEN", payload: localStorage.getItem("refreshToken") })
			}
			else if (validateTokenStore.error) {
				toast.error(validateTokenStore.message);
				// Don't redirect to amp in local environment
				// eslint-disable-next-line
				if (isLocalVariable == '0') {
					window.location.href = `${process.env.REACT_APP_AMP_URL}/#/auth/home/reg-user`;
				}
			}
		}
	}, [dispatch, isLocalVariable, setTimers, validateExternalTokenReqSent, validateTokenStore]);


	useEffect(() => {
		if (validateInternalTokenReqSent) {
			if (validateInternalTokenStore.success) {
				localStorage.setItem("authToken", validateInternalTokenStore.admin_user_token as string);
				localStorage.setItem("username", validateInternalTokenStore.username as string);
				localStorage.setItem("sessionExp", String(validateInternalTokenStore.exp))
				validateInternalTokenStore.validationToken && localStorage.setItem("validationToken", validateInternalTokenStore.validationToken);
				sessionStorage.setItem("isInternalUser", "true");
				setIsInternalUser(true);
				(localStorage.getItem("sessionExp") !== null) && setTimers();
				setAuth(true);
				setValidateInternalTokenReqSent(false);
			}
			else if (validateInternalTokenStore.error) {
				toast.error(validateInternalTokenStore.message as string);
				window.location.href = `${process.env.REACT_APP_INTERNAL_PORTAL_URL}#/login`;
			}
		}
	}, [validateInternalTokenStore, validateInternalTokenReqSent, setTimers]);

	useEffect(() => {
		if (validateRefreshTokenReqSent) {
			if (refreshTokenStore.valid && auth && !sessionExpired) {
				refreshTokenStore.token && localStorage.setItem("authToken", refreshTokenStore.token);
				refreshTokenStore.exp && localStorage.setItem("sessionExp", String(refreshTokenStore.exp));
				refreshTokenStore.refresh && localStorage.setItem("refreshToken", refreshTokenStore.refresh);
				refreshTokenStore.validationToken && localStorage.setItem("validationToken", refreshTokenStore.validationToken);
				refreshTokenStore.exp && toast.success(`${t("extension-success")} ${formatUnixTimestamp(refreshTokenStore.exp, "YYYY/MM/DD hh:mm:ss A")}`);
				(localStorage.getItem("sessionExp") !== null) && setTimers();
				setValidateRefreshTokenReqSent(false);
			} else if (!refreshTokenStore.valid && refreshTokenStore.message) {
				toast.error(t("failed-extension") as string);
			}
		}
		dispatch(resetValidateRefreshToken());
	}, [refreshTokenStore, auth, sessionExpired, validateRefreshTokenReqSent, dispatch, setTimers, t])

	const extendSession = () => {
		const refreshToken = process.env.REACT_APP_IS_LOCAL === "1"
			? process.env.REACT_APP_REFRESH_TOKEN
			: window.localStorage.getItem("refreshToken");
		if (refreshToken) {
			dispatch(validateRefreshToken(refreshToken));
			setValidateRefreshTokenReqSent(true);
		} else {
			console.log("Refresh token not found");
			toast.error(t("failed-extension") as string);
		}
		setShowSessionModal("hide");
	}

	const routes = generateRoutes(auth, sessionExpired);
	return (
		<>
			<main>
				<NavigationProvider>
					<Router>
						<Routes>
							{routes}
							<Route path="*"
								element={(auth && timerIsSet) && (isInternalUser ? <Navigate to={"/internal-landing"} /> : <Navigate to={"/landing"} />)} />
						</Routes>
					</Router>
					{showSessionModal !== "hide" && <Modal class="session-modal" onCloseModal={() => { setShowSessionModal("hide"); if (showSessionModal === "expired") { window.location.href = `${process.env.REACT_APP_AMP_URL}/#/auth/home/reg-user`; } }} >
						<LoadingSpinner loading={refreshTokenStore.loading}>
							<div>
								<h3>{showSessionModal === "extend" ? t("expiring") : t("expired")}</h3>
								{!isInternalUser && <p>{showSessionModal === "extend" ? t("extend-question") : t("login")}</p>}
								<div className='session-modal-buttons'>
									{(!isInternalUser && showSessionModal === "extend") && <Button type='secondary' onClick={() => extendSession()} >
										{t("extend")}
									</Button>}
									{showSessionModal === "extend" && <Button type='cancel' onClick={() => setShowSessionModal("hide")} >
										{t("cancel")}
									</Button>}
									{showSessionModal === "expired" && <Button type='secondary' onClick={() => { setShowSessionModal("hide"); window.location.href = `${process.env.REACT_APP_AMP_URL}/#/auth/home/reg-user`; }} >
										{t("ok")}
									</Button>}
								</div>

							</div>
						</LoadingSpinner>
					</Modal>}
				</NavigationProvider>
			</main>
		</>
	)
}

export default App;
